import * as React       from "react";
import {cx} from "emotion";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheck} from '@fortawesome/free-solid-svg-icons';

import "./password-update-modal.scss";

import {bindInput}      from "@Common/react/bind-input";
import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {ComponentLink}  from "@Common/react/cst2";
import {Load2}          from "@Common/react/load2";
import {scopedState}    from "@Common/react/scoped-state";
import {UseState}       from "@Common/react/use-state";
import {ModalService3}  from "@Common/ui-components/modal-service-3/modal-service-3";
import {PasswordInput}  from "@Common/ui-components/password-input/password-input";
import {parseUrlQuery}  from "@Common/utils/parse-url-query";
import {AGENT_LEVEL}    from "@Xpresso/routes/common/logic/user-level";
import {ModalLayout}    from "../../../common/modal-layout/modal-layout";

export const PasswordUpdateModal = ({
    next: rootNext,
    title,
    onSave,
    targetUserId,
    urlParam,
}: {
    next: ComponentLink<typeof ModalService3<any>, "rootNext">,
    title: string,
    onSave: (updateFields: any) => any,
    targetUserId?: number,
    urlParam: string,
}) => cs(
    consumeContext("apis"),
    ["uploading", (_, next) => UseState({next})],
    ["query", ({}, next) => next(parseUrlQuery(window.location.search))],
    ["modal", ({query}, next) => ModalService3({
        initShow: query?.['modal'] === "password",
        xdQaName: "Password Update Modal",
        width: 600,
        next,
        urlParam,
        rootNext,
    })],
    ["state", (_, next) => UseState({
        next,
        initValue: {password: ''}
    })],
    ["validation", ({state}, next) => next({
        length: state.value?.password?.length >= 6,
        number: /\d/.test(state.value?.password),
        lowercase: state.value?.password && /[a-z]/.test(state.value?.password),
        uppercase: /[A-Z]/.test(state.value?.password),
        special: /[@$!%*#?&]/.test(state.value?.password),
        matches: state.value?.password === state.value?.matchPassword,
        full: /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{6,}$/.test(state.value?.password) && state.value?.password === state.value?.matchPassword
    })],
    ({uploading, state, modal, validation}, next) => ModalLayout({
        title,
        content: next(),
        buttons: [
            {
                label: "Cancel",
                onClick: () => {
                    state.onChange({});
                    modal.close();
                },
            },
            {
                label: "Save",
                disabled: uploading.value || !validation.full,
                primary: true,
                onClick: async () => {
                    uploading.onChange(true);
                    const updateFields = {
                        password: state.value.password.trim(),
                        ...(targetUserId && {userId: targetUserId}),
                    };
                    const result = await onSave(updateFields);
                    if (result?.errorMessage) {
                        alert(result.errorMessage);
                    } else if (result?.errorInfo) {
                        alert(result.errorInfo);
                    } else {
                        modal.close();
                    }
                    uploading.onChange(false);
                },
            },
        ],
    }),
    ({state, validation}) => cs(
        consumeContext("session"),
        consumeContext("apis"),
        consumeContext("userLevels"),
        ["userProfile", ({apis, session}, next) => Load2({
            _key: JSON.stringify({...session.value}),
            fetch: async () => apis.next.userProfile.getUserProfile(targetUserId),
            next,
        })],
        ({userLevels, userProfile}) => (
            <div className="password-input-modal-q9k">
                {userLevels?.userLevel < AGENT_LEVEL &&
                    <div className="input-card">
                        <div className="label">User Name:</div>
                        <div className="username" xd-qa-type="label" xd-qa-name="User Name">{userProfile?.loading ? "Loading..." : userProfile?.value?.username || "N/A"}</div>
                    </div>
                }
                <div className="input-card">
                    <div className="label">Enter a New Password</div>
                    {PasswordInput({inputProps:{
                        ...bindInput(scopedState(state, ["password"])),
                        className: "value",
                        maxLength: 64,
                        "mapping": "password",
                        "xd-qa-type": "label",
                        "xd-qa-name": "Enter a New Password",
                        autoComplete: "new-password",
                    }})}
                </div>
                <div className="validation-card">
                    <div className="validation-line">
                        {validation.length ? (
                            <div className="validation-icon"><FontAwesomeIcon className={"icon"} icon={faCheck} xd-qa-type="icon" xd-qa-name="At least 6 characters check icon"/></div>
                        ) : (
                            <div className="validation-spacer"/>
                        )}
                        <div className={cx("validation-label", {validated: validation.length})} xd-qa-type="label" xd-qa-name="At least 6 characters">At least 6 characters</div>
                    </div>
                    <div className="validation-line">
                        {validation.number ? (
                            <div className="validation-icon"><FontAwesomeIcon className={"icon"} icon={faCheck} xd-qa-type="icon" xd-qa-name="At least 1 number check icon"/></div>
                        ) : (
                            <div className="validation-spacer"/>
                        )}
                        <div className={cx("validation-label", {validated: validation.number})} xd-qa-type="label" xd-qa-name="At least 1 number">At least 1 number</div>
                    </div>
                    <div className="validation-line">
                        {validation.lowercase ? (
                            <div className="validation-icon"><FontAwesomeIcon className={"icon"} icon={faCheck} xd-qa-type="icon" xd-qa-name="At least 1 lowercase letter check icon"/></div>
                        ) : (
                            <div className="validation-spacer"/>
                        )}
                        <div className={cx("validation-label", {validated: validation.lowercase})} xd-qa-type="label" xd-qa-name="At least 1 lowercase letter">At least 1 lowercase letter</div>
                    </div>
                    <div className="validation-line">
                        {validation.uppercase ? (
                            <div className="validation-icon"><FontAwesomeIcon className={"icon"} icon={faCheck} xd-qa-type="icon" xd-qa-name="At least 1 uppercase letter check icon"/></div>
                        ) : (
                            <div className="validation-spacer"/>
                        )}
                        <div className={cx("validation-label", {validated: validation.uppercase})} xd-qa-type="label" xd-qa-name="At least 1 uppercase letter">At least 1 uppercase letter</div>
                    </div>
                    <div className="validation-line">
                        {validation.special ? (
                            <div className="validation-icon"><FontAwesomeIcon className={"icon"} icon={faCheck} xd-qa-type="icon" xd-qa-name="At least 1 special character check icon"/></div>
                        ) : (
                            <div className="validation-spacer"/>
                        )}
                        <div className={cx("validation-label", {validated: validation.special})} xd-qa-type="label" xd-qa-name="At least 1 special character">At least 1 special character @$!%*#?&</div>
                    </div>
                </div>
                <div className="input-card">
                    <div className="label">Confirm New Password</div>
                    {PasswordInput({inputProps:{
                        ...bindInput(scopedState(state, ["matchPassword"])),
                        className: "value",
                        maxLength: 64,
                        "xd-qa-type": "label",
                        "xd-qa-name": "Confirm New Password",
                        autoComplete: "new-password",
                    }})}
                </div>
                <div className="validation-card">
                    <div className="validation-line">
                        {validation.matches ? (
                            <div className="validation-icon"><FontAwesomeIcon className={"icon"} icon={faCheck} xd-qa-type="icon" xd-qa-name="Passwords Match check icon"/></div>
                        ) : (
                            <div className="validation-spacer"/>
                        )}
                        <div className={cx("validation-label", {validated: validation.matches})} xd-qa-type="label" xd-qa-name="Passwords Match">Passwords Match</div>
                    </div>
                </div>
            </div>
        )
    )
);
